import React, { useEffect, useCallback, useMemo } from 'react'
import { Paper, Box, IconButton, ClickAwayListener, useMediaQuery, Typography } from '@mui/material'
import { makeStyles, useTheme } from '@mui/styles'
import Popper from '@mui/material/Popper'
import type { Instance, Modifier } from '@popperjs/core'
import CloseIcon from '@mui/icons-material/Close'
import ListIcon from '@mui/icons-material/FormatListBulleted'
import AddIcon from '@mui/icons-material/Add'
import SettingsIcon from '@mui/icons-material/SettingsOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Draggable from 'react-draggable'

import lang from '@/lang'
import AiChatContent from './ai-chat-content'
import ThemeInterface from '@/theme-interface'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import {
  selectPopperSize,
  selectAiChatView,
  selectPopperPosition,
  setPopperSize,
  setPopperPosition,
  setAiChatView,
  createNewConversation,
  AiChatView,
} from '@/redux/ducks/ai/chat'
import AiChatConversationList from '@/modules/base/components/ai/chat/ai-chat-conversation-list/ai-chat-conversation-list'
import AiChatSettings from './ai-chat-settings'
import { useDraggableResizable, Position, Size } from '@/modules/base/components/draggable-resizable-modal'

const DEFAULT_WIDTH = 500
const DEFAULT_HEIGHT = 600
const MIN_WIDTH = 400
const MIN_HEIGHT = 300

const useStyles = makeStyles((theme: ThemeInterface) => ({
  popper: {
    zIndex: 90,
    pointerEvents: 'none',
    [theme.breakpoints.down(764)]: {
      zIndex: theme.zIndex.appBar + 1,
    },
    [theme.devices.mobile]: {
      position: 'fixed',
      inset: '0 !important',
      transform: 'none !important',
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    pointerEvents: 'auto',
    [theme.devices.mobile]: {
      width: '100vw !important',
      height: '100vh !important',
      borderRadius: 0,
    },
  },
  header: {
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    userSelect: 'none',
    borderBottom: theme.border,
    position: 'relative',
    pointerEvents: 'auto',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.textMuted,
  },
  contentWrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative',
    pointerEvents: 'auto',
  },
  resizeHandle: {
    position: 'absolute',
    right: 3,
    bottom: 3,
    width: 20,
    height: 20,
    cursor: 'nwse-resize',
    zIndex: 10,
    pointerEvents: 'auto',
    touchAction: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 2,
      bottom: 2,
      width: 10,
      height: 10,
      borderRight: `2px solid ${theme.textMuted}`,
      borderBottom: `2px solid ${theme.textMuted}`,
    },
  },
  view: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 0,
    paddingBottom: 15,
  },
  footer: {
    textAlign: 'center',
    marginBottom: 10,
  },
}))

interface AiChatPopperProps {
  open: boolean
  anchorEl: Element | null
  onClose: () => void
  popperRef: React.RefObject<Instance | null>
  blockClickAway?: boolean
}

export default function AiChatPopper(props: AiChatPopperProps) {
  const { open, anchorEl, popperRef, onClose, blockClickAway = false } = props
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const popperPosition = useAppSelector(selectPopperPosition)
  const storedSize = useAppSelector(selectPopperSize)
  const view = useAppSelector(selectAiChatView)

  const smallerScreen = useMediaQuery(theme.breakpoints.down(992))

  // Callbacks for Redux state updates
  const handlePositionChange = useCallback(
    (newPosition: Position) => {
      if (!smallerScreen) {
        dispatch(setPopperPosition(newPosition))
      }
    },
    [dispatch, smallerScreen],
  )

  const handleResizeEnd = useCallback(
    (newSize: Size) => {
      if (!smallerScreen) {
        dispatch(setPopperSize(newSize))
      }
    },
    [dispatch, smallerScreen],
  )

  const handleDragEnd = useCallback(
    (newPosition: Position) => {
      if (!smallerScreen) {
        dispatch(setPopperPosition(newPosition))
      }
      popperRef.current?.update()
    },
    [dispatch, smallerScreen, popperRef],
  )

  // Memoize size to prevent infinite loops
  const initialSize = useMemo(() => ({
    width: storedSize?.width || DEFAULT_WIDTH,
    height: storedSize?.height || DEFAULT_HEIGHT,
  }), [storedSize?.width, storedSize?.height])

  // Use the shared draggable/resizable hook
  const {
    currentSize,
    isDragging,
    paperRef,
    handleDragStart,
    handleDragStop,
    handleResizeStart,
    bounds,
    isResizingRef,
    blockInteractionRef,
  } = useDraggableResizable({
    size: initialSize,
    position: popperPosition,
    onPositionChange: handlePositionChange,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    anchorEl: anchorEl as HTMLElement | null,
    open,
    onResizeEnd: handleResizeEnd,
    onDragEnd: handleDragEnd,
    disabled: smallerScreen,
  })

  // Save size to Redux when component unmounts
  useEffect(() => {
    return () => {
      if (!smallerScreen) {
        dispatch(setPopperSize(currentSize))
      }
    }
  }, [])

  const handleMenuClick = useCallback(() => {
    const newView = view === AiChatView.CONVERSATION_LIST ? AiChatView.CHAT : AiChatView.CONVERSATION_LIST
    dispatch(setAiChatView(newView))
  }, [view, dispatch])

  const handleBackClick = useCallback(() => {
    dispatch(setAiChatView(AiChatView.CHAT))
  }, [dispatch])

  const handleNewChatClick = useCallback(() => {
    dispatch(createNewConversation())
    dispatch(setAiChatView(AiChatView.CHAT))
  }, [dispatch])

  const handleSettingsClick = useCallback(() => {
    dispatch(setAiChatView(AiChatView.SETTINGS))
  }, [dispatch])

  // Popper modifiers for positioning
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popperModifiers: Partial<Modifier<any, any>>[] = useMemo(() => {
    // If custom position is set, disable all positioning from Popper.js
    if (popperPosition && !smallerScreen) {
      return [
        { name: 'offset', enabled: false },
        { name: 'flip', enabled: false },
        { name: 'preventOverflow', enabled: false },
        { name: 'computeStyles', enabled: false },
        { name: 'applyStyles', enabled: false },
      ]
    }

    // Default positioning when no custom position
    return [
      { name: 'offset', options: { offset: [0, 12] } },
      { name: 'flip', enabled: false },
      { name: 'preventOverflow', options: { padding: 0, altAxis: true, boundary: 'viewport' } },
    ]
  }, [popperPosition, smallerScreen])

  const paperStyle: React.CSSProperties = smallerScreen
    ? {}
    : {
      width: currentSize.width,
      height: currentSize.height,
      minWidth: currentSize.width,
      maxWidth: currentSize.width,
      minHeight: currentSize.height,
      maxHeight: currentSize.height,
    }

  const activeView = useMemo(() => {
    let contentView = null
    switch (view) {
      case AiChatView.CONVERSATION_LIST:
        contentView = <AiChatConversationList onConversationSelect={handleBackClick} />
        break
      case AiChatView.CHAT:
        contentView = <AiChatContent />
        break
      case AiChatView.SETTINGS:
        contentView = <AiChatSettings />
        break
      default:
        contentView = null
        break
    }

    return (
      <Box className={classes.view}>
        {contentView}
      </Box>
    )
  }, [view, handleBackClick, classes.view])

  const handleClickAway = useCallback((event: MouseEvent | TouchEvent) => {
    if (blockClickAway || blockInteractionRef.current || isDragging || isResizingRef.current) return

    const target = event.target as HTMLElement

    // Don't close if clicking inside the paper element
    if (paperRef.current?.contains(target)) {
      return
    }

    // Don't close if clicking on the button or its children
    if (target.closest('[data-ai-chat-button="true"]')) {
      return
    }

    onClose()
  }, [blockClickAway, isDragging, onClose, paperRef, isResizingRef, blockInteractionRef])

  const popperContent = (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleClickAway}
    >
      <Draggable
        position={popperPosition && !smallerScreen ? popperPosition : { x: 0, y: 0 }}
        onStart={handleDragStart}
        onStop={handleDragStop}
        disabled={smallerScreen}
        nodeRef={paperRef}
        handle=".ai-chat-header-drag-handle"
        bounds={smallerScreen ? undefined : bounds}
      >
        <Paper
          elevation={6}
          className={classes.paper}
          style={{
            ...paperStyle,
            maxWidth: '100vw',
            maxHeight: '100vh',
          }}
          ref={paperRef}
        >
          {/* Header */}
          <Box
            className={`${classes.header} ai-chat-header-drag-handle`}
            sx={{
              cursor: isDragging ? 'grabbing' : (smallerScreen ? 'default' : 'grab'),
            }}
          >
            <Box className={classes.headerLeft}>
              <IconButton
                size="small"
                onPointerUp={handleMenuClick}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                title={lang.ai.chat.header.menu}
              >
                <ListIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onPointerUp={handleNewChatClick}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                title={lang.ai.chat.header.newChat}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box className={classes.headerCenter}>
              {!smallerScreen && (
                <Box className={classes.dragHandle} title={lang.ai.chat.header.dragHandle}>
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              )}
            </Box>

            <Box className={classes.headerRight}>
              {view !== AiChatView.CHAT && (
                <IconButton
                  size="small"
                  onPointerUp={handleBackClick}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  title={lang.ai.chat.header.back}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onPointerUp={handleSettingsClick}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                title={lang.ai.chat.header.settings}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onPointerUp={onClose}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                title={lang.ai.chat.header.close}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box className={classes.contentWrapper}>
            {activeView}
          </Box>

          <Box className={classes.footer}>
            <Typography variant="caption" color={theme.textMuted} dangerouslySetInnerHTML={{ __html: lang.ai.chat.footer.description }} />
          </Box>
          {/* Resize Handle */}
          {!smallerScreen && (
            <Box
              className={classes.resizeHandle}
              onPointerDown={handleResizeStart}
            />
          )}
        </Paper>
      </Draggable>
    </ClickAwayListener>
  )

  // If we have a custom position, render as fixed positioned element
  if (popperPosition && !smallerScreen && open) {
    return (
      <Box
        className={classes.popper}
        sx={{
          position: 'fixed',
          inset: 'auto',
          pointerEvents: 'none',
        }}
      >
        {popperContent}
      </Box>
    )
  }

  // Otherwise use Popper for positioning relative to button
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-end"
      popperRef={popperRef}
      className={classes.popper}
      modifiers={popperModifiers}
    >
      {popperContent}
    </Popper>
  )
}
